import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { DataManager, Visualisation, Toolbar, FilterBar } from 'wc-visualisation';
import { Table, Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv } from 'wc-lib';
import * as screenfull from 'screenfull';
import { sortCsv } from '../../../helpers/stringHelper';
import { fixCurrencyFieldsForTable } from './dataUtils';
// import { FPP_RESIDUAL_COST_RATE_1HR_testData } from './test-data/FPP_RESIDUAL_COST_RATE_1HR-test-data';
// import { FPP_RESIDUAL_COST_RATE_3HR_testData } from './test-data/FPP_RESIDUAL_COST_RATE_3HR-test-data';
// import { FPP_RESIDUAL_COST_RATE_24HR_testData } from './test-data/FPP_RESIDUAL_COST_RATE_24HR-test-data';
// import { FPP_PERFORMANCE_COST_RATE_1HR_testData } from './test-data/FPP_PERFORMANCE_COST_RATE_1HR-test-data';
// import { FPP_PERFORMANCE_COST_RATE_3HR_testData } from './test-data/FPP_PERFORMANCE_COST_RATE_3HR-test-data';
// import { FPP_PERFORMANCE_COST_RATE_24HR_testData } from './test-data/FPP_PERFORMANCE_COST_RATE_24HR-test-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./fpp-cost-rate.html'),
  name: 'fpp-cost-rate',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'wc-table': Table,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class FppCostRate extends Vue {
  selectedTab: string = '';
  tabs = [{ value: 'PERFORMANCE', label: 'Performance Cost Rate' }, { value: 'RESIDUAL', label: 'Residual Cost Rate' }]

  selectedInterval: string = '';
  intervals = [{ value: '1HR', label: '1 hr' }, { value: '3HR', label: '3 hrs' }, { value: '24HR', label: '24 hrs' }]

  dataCache: Record<string, any[]> = {};
  data: any[] = [];
  tableData: any[] = null;
  tableColumns: any[] = [];
  loadMessage: string = '';

  intervalRefresh: any;

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    this.selectedTab = this.tabs[0].value;
    this.selectedInterval = this.intervals[0].value;
    this.loadData(this.selectedTab, this.selectedInterval, true);

    this.intervalRefresh = setInterval(() => {
      this.loadData(this.selectedTab, this.selectedInterval, true);
    }, 300 * 1000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.loadData(this.selectedTab, this.selectedInterval, false);
  }

  @Watch('selectedInterval')
  onSelectedIntervalChange(newValue: string, oldValue: string): void {
    this.selectedInterval = newValue;
    this.loadData(this.selectedTab, this.selectedInterval, false);
  }

  loadData(tab: string, interval: string, clearDataCache: boolean) {
    this.loadMessage = 'Loading data';
    this.fetchDataAsync(tab, interval, clearDataCache).then(
      (data) => {
        this.data = data;
        this.tableData = this.getTableData(data, tab);
        this.tableColumns = this.getTableColumns(tab);
        this.loadMessage = '';
      },
      (error) => {
        this.loadMessage = 'No data found';
      });
  }

  async fetchDataAsync(tab: string, interval: string, clearDataCache: boolean): Promise<any[]> {
    if (clearDataCache) {
      this.dataCache = {};
    }

    let reportName = `FPP_${tab}_COST_RATE_${interval}`;

    if (!this.dataCache[reportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + reportName, undefined, 60000, reportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['INTERVAL_DATETIME']);
      let data = DataManager.getDynamicView(id, reportName).data();

      // let data: any[];
      // if (tab === 'PERFORMANCE' && interval === '1HR') data = FPP_PERFORMANCE_COST_RATE_1HR_testData;
      // else if (tab === 'PERFORMANCE' && interval === '3HR') data = FPP_PERFORMANCE_COST_RATE_3HR_testData;
      // else if (tab === 'PERFORMANCE' && interval === '24HR') data = FPP_PERFORMANCE_COST_RATE_24HR_testData;
      // else if (tab === 'RESIDUAL' && interval === '1HR') data = FPP_RESIDUAL_COST_RATE_1HR_testData;
      // else if (tab === 'RESIDUAL' && interval === '3HR') data = FPP_RESIDUAL_COST_RATE_3HR_testData;
      // else if (tab === 'RESIDUAL' && interval === '24HR') data = FPP_RESIDUAL_COST_RATE_24HR_testData;
      // data = data.map((x) => ({...x, INTERVAL_DATETIME: new Date(Date.parse(x.INTERVAL_DATETIME)) }));

      this.dataCache[reportName] = data
        .map((x) => ({ ...x, RELEVANT_REGIONS: sortCsv(x.RELEVANT_REGIONS) })); // fix unsorted RELEVANT_REGIONS column
    }

    return this.dataCache[reportName];
  }

  getTableData(data: any[], tab: string): any[] {
    if (tab === 'PERFORMANCE') {
      return fixCurrencyFieldsForTable(data, ['FPP_PAYMENT_RATE', 'FPP_RECOVERY_RATE', 'USED_FCAS_RATE', 'UNUSED_FCAS_RATE']);
    };

    if (tab === 'RESIDUAL') {
      return fixCurrencyFieldsForTable(data, ['FPP', 'USED_FCAS', 'UNUSED_FCAS']);
    }

    return null;
  }

  getTableColumns(tab: string): any[] {
    if (tab === 'PERFORMANCE') return this.performanceTableColumns;
    if (tab === 'RESIDUAL') return this.residualTableColumns;
    return null;
  }

  //
  // table
  //

  performanceTableColumns = [
    {
      title: 'Interval Date Time',
      field: 'INTERVAL_DATETIME',
      formatter: (value) => {
        let date = value.INTERVAL_DATETIME || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Constraint ID',
      field: 'CONSTRAINTID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Version #',
      field: 'VERSIONNO',
      fieldType: FieldType.Number,
      sortable: true
    },
    {
      title: 'Bid Type',
      field: 'BIDTYPE',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Relevant Regions',
      field: 'RELEVANT_REGIONS',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'FPP Payment Rate ($/MWHz)',
      field: 'FPP_PAYMENT_RATE',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'FPP Recovery Rate ($/MWHz)',
      field: 'FPP_RECOVERY_RATE',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'Used FCAS Rate ($/MWHz)',
      field: 'USED_FCAS_RATE',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'Unused FCAS Rate ($/MWHz)',
      field: 'UNUSED_FCAS_RATE',
      fieldType: FieldType.Dollar,
      sortable: true
    },
  ];

  residualTableColumns = [
    {
      title: 'Interval Date Time',
      field: 'INTERVAL_DATETIME',
      formatter: (value) => {
        let date = value.INTERVAL_DATETIME || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Constraint ID',
      field: 'CONSTRAINTID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Version #',
      field: 'VERSIONNO',
      fieldType: FieldType.Number,
      sortable: true
    },
    {
      title: 'Bid Type',
      field: 'BIDTYPE',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Relevant Regions',
      field: 'RELEVANT_REGIONS',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Residual FPP Rate ($/MWh)',
      field: 'FPP',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'Residual Used FCAS ($/MWh)',
      field: 'USED_FCAS',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'Residual Unused FCAS ($/MWh)',
      field: 'UNUSED_FCAS',
      fieldType: FieldType.Dollar,
      sortable: true
    },
  ];

  download() {
    let rows = this.data;
    let columns = this.tableColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(`FPP_${this.selectedTab}_COST_RATE_${this.selectedInterval}_${formatDate(rows[0].INTERVAL_DATETIME, 'yyyyMMddHHmm')}.csv`, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
    setTimeout(() => {
      if (!this.isFullscreen) {
        this.loadData(this.selectedTab, this.selectedInterval, false);
      }
    }, 2000); // delay gives screen.isFullscreen time to update after screen.toggle()
  }
}


// WEBPACK FOOTER //
// src/views/electricity/fpp/fpp-cost-rate.ts